'use client'

import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

// Ref-count global para soportar sheets apilados (p. ej. Upsell → Carrito,
// que se solapan durante la transición de AnimatePresence): el scroll del fondo
// solo se restaura cuando el ÚLTIMO sheet se cierra.
let lockCount = 0
let savedOverflow = ''

/** Bloquea el scroll del <body> mientras el componente esté montado. */
export function useScrollLock() {
  useEffect(() => {
    if (lockCount === 0) {
      savedOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
    }
    lockCount += 1
    return () => {
      lockCount -= 1
      if (lockCount === 0) {
        document.body.style.overflow = savedOverflow
      }
    }
  }, [])
}

/**
 * Renderiza children en un portal a <body> y bloquea el scroll del fondo.
 *
 * Portalear es OBLIGATORIO para el modal de modificadores: vive dentro de
 * <Reveal>, que deja `will-change: transform` permanente. Esa propiedad crea un
 * containing block que rompe `position: fixed`, haciendo que el modal scrollee
 * con la página en vez de quedar fijo al viewport (y empuja el botón fuera de la
 * pantalla). Montando en <body>, el `fixed inset-0` vuelve a ser relativo al
 * viewport. Robusto ante cualquier ancestro transformado, presente o futuro.
 */
export function SheetPortal({ children }: { children: ReactNode }) {
  useScrollLock()
  // Estos sheets solo se montan tras una interacción del cliente (nunca en SSR),
  // así que document siempre existe aquí; el guard es solo defensivo.
  if (typeof document === 'undefined') return null
  return createPortal(children, document.body)
}
